import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CreditCard, Package, AlertTriangle } from "lucide-react";
import { UrgentAck } from "./UrgentAck";

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {value !== null && <span className="font-medium">${value.toFixed(2)}</span>}
    </div>
  );
}

export default function QuoteSidebar({ addressesVerified, pickup, dropoff, quoting, quote, creditBalance, onAccount, submitting, onBook, share, service, urgentAck, setUrgentAck }) {
  const [stripeMode, setStripeMode] = useState("test");
  useEffect(() => { api.get("/payments/config").then((r) => setStripeMode(r.data.mode || "test")).catch(() => {}); }, []);
  return (
    <Card className="sticky top-20 border-kilted-green/20">
      <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Package className="h-5 w-5 text-kilted-gold" /> Your quote</CardTitle></CardHeader>
      <CardContent>
        {!addressesVerified ? (
          (pickup && dropoff) ? (
            <p className="flex items-start gap-1.5 text-sm text-amber-700" data-testid="address-guard-note">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> One or both addresses aren't verified. Please choose them from the suggestions so we can map and price your booking.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="quote-placeholder">Select both addresses to see your price.</p>
          )
        ) : quoting ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : quote ? (
          <div data-testid="quote-breakdown">
            {quote.items?.length ? (
              <div className="mb-2 space-y-1" data-testid="quote-items">
                {quote.items.map((it, i) => (
                  <div key={`${it.size}-${it.quantity}-${i}`} className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="capitalize">{it.quantity}× {it.size}</span>
                    {it.size_surcharge > 0 && <span>+${it.size_surcharge.toFixed(2)}</span>}
                  </div>
                ))}
                <div className="my-2 border-t border-border" />
              </div>
            ) : null}
            {quote.rate_tier === "transfer" ? (
              <>
                <p className="text-[11px] font-medium text-kilted-green" data-testid="quote-transfer-note">{quote.transfer_name} — flat price per parcel</p>
                <Row label="Transfer price" value={quote.subtotal} />
              </>
            ) : (
              <>
                <Row label="Base fare" value={quote.base_fare} />
                <Row label={`Distance (${quote.distance_km} km${quote.per_km != null ? ` @ $${Number(quote.per_km).toFixed(2)}/km` : ""}${quote.km_loading_pct ? ` +${quote.km_loading_pct}% multi-package` : ""})`} value={quote.distance_cost} />
                {quote.rate_tier === "account" && <p className="text-[11px] font-medium text-kilted-green" data-testid="quote-account-rate-note">Account rate applied</p>}
                <Row label="Size surcharge" value={quote.size_surcharge} />
                <Row label={`Fare subtotal ×${quote.service_multiplier}`} value={quote.subtotal} />
              </>
            )}
            {quote.fuel_levy ? <Row label={`Fuel levy (${quote.fuel_levy_pct ?? 0}% of subtotal)`} value={quote.fuel_levy} /> : null}
            <div className="my-2 border-t border-border" />
            <Row label="Subtotal (ex GST)" value={quote.net ?? quote.subtotal} />
            <Row label={`GST (${Math.round((quote.gst_rate ?? 0.1) * 100)}%)`} value={quote.gst ?? 0} />
            <div className="mt-3 flex items-center justify-between rounded-md bg-kilted-green px-3 py-2 text-white">
              <span className="font-semibold">Total (inc GST)</span>
              <span className="font-heading text-xl font-extrabold" data-testid="quote-total">${quote.total.toFixed(2)}</span>
            </div>
            {!onAccount && (
              <p className="mt-2 text-center text-[11px] text-muted-foreground" data-testid="quote-no-surcharge-note">
                No card surcharge — you pay exactly this amount at checkout.
              </p>
            )}
            {creditBalance > 0 && (
              <div className="mt-2 flex items-center justify-between rounded-md border border-kilted-gold/50 bg-kilted-gold/10 px-3 py-2 text-xs" data-testid="quote-credit-note">
                <span className="font-medium text-kilted-green">Referral credit {onAccount ? "applied to this booking" : "applied at payment"}</span>
                <span className="font-bold text-kilted-green">−${Math.min(creditBalance, quote.total).toFixed(2)}</span>
              </div>
            )}
            {service === "sameday" && <UrgentAck checked={urgentAck} onChange={setUrgentAck} />}
            <Button onClick={onBook} disabled={submitting || (service === "sameday" && !urgentAck)} data-testid="book-pay-button" className="mt-4 w-full gap-2 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {onAccount ? "Confirm booking" : "Book & pay"}
            </Button>
            {share && <div className="mt-2">{share}</div>}
            <p className="mt-2 text-center text-[11px] text-muted-foreground">{onAccount ? "Billed to your monthly account" : stripeMode === "live" ? "Secured by Stripe" : "Secured by Stripe (test mode)"}</p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
